import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, X, MessageCircle } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import { WHATSAPP_LINK } from '../utils/constants';
import { COMPARISONS, COMPARISON_LIST } from '../data/comparisonPages';

export default function Comparison({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const data = COMPARISONS[slug];
  if (!data) return <Navigate to="/" replace />;

  const path = `/moneyfeed-vs-${data.slug}`;
  const others = COMPARISON_LIST.filter((c) => c.slug !== data.slug);
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: data.h1, url: `https://moneyfeed.io${path}`, description: data.seoDescription },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid={`comparison-${data.slug}-page`}>
      <Seo title={data.seoTitle} description={data.seoDescription} path={path} jsonLd={jsonLd} />
      <LandingHeader />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${data.accent}1A` }}>
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: data.accent }}>MoneyFeed vs {data.competitor}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-4">{data.h1}</h1>
        <p className="text-base sm:text-lg text-gray-700 font-semibold mb-3">{data.tagline}</p>
        <p className="text-base text-gray-600 mb-8 max-w-2xl mx-auto">{data.intro}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/auth">
            <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="comparison-get-started">
              Start free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full sm:w-auto border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold rounded-full px-7 py-6 text-base" data-testid="comparison-whatsapp">
              <MessageCircle className="h-4 w-4 mr-1" /> Try it on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-md overflow-hidden" data-testid="comparison-table">
          <div className="grid grid-cols-3 bg-[#064E3B] text-white text-sm font-bold">
            <div className="p-4"></div>
            <div className="p-4 text-center">MoneyFeed</div>
            <div className="p-4 text-center" style={{ color: '#D1FAE5' }}>{data.competitor}</div>
          </div>
          {data.rows.map((r, i) => (
            <div key={i} className={`grid grid-cols-3 text-sm border-t border-gray-100 ${i % 2 ? 'bg-[#F8FAFC]' : 'bg-white'}`} data-testid={`comparison-row-${i}`}>
              <div className="p-4 font-semibold text-[#064E3B]">{r.feature}</div>
              <div className="p-4 text-gray-700 flex items-start gap-2">
                <Check className="h-4 w-4 text-[#65A30D] flex-shrink-0 mt-0.5" /> <span>{r.mf}</span>
              </div>
              <div className="p-4 text-gray-500 flex items-start gap-2">
                <span>{r.them}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-md p-6 border-t-4 border-[#84CC16]" data-testid="comparison-who-mf">
            <h3 className="font-bold text-[#064E3B] mb-2">Choose MoneyFeed if…</h3>
            <p className="text-gray-600 text-sm">{data.whoMf}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-6 border-t-4" style={{ borderColor: data.accent }} data-testid="comparison-who-them">
            <h3 className="font-bold text-[#064E3B] mb-2">Choose {data.competitor} if…</h3>
            <p className="text-gray-600 text-sm">{data.whoThem}</p>
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 mt-6 max-w-2xl mx-auto">{data.keepNote}</p>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-8">Frequently asked questions</h2>
        <div className="space-y-4">
          {data.faqs.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`comparison-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">See why freelancers switch to MoneyFeed</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Start free and text your first expense in the next 30 seconds — no spreadsheets, no learning curve.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="comparison-cta-signup">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          {others.map((o) => (
            <Link key={o.slug} to={`/moneyfeed-vs-${o.slug}`} className="hover:text-[#064E3B]">vs {o.competitor}</Link>
          ))}
          <Link to="/business" className="hover:text-[#064E3B]">For Business</Link>
        </div>
      </footer>
    </div>
  );
}
